/**
 * VoiceHistoryLog — voice transcript history rendering.
 *
 * Reads `history`, `clearHistory`, `settings.voiceAgentName` from
 * `useVoiceStore`. Owns local state for `expandedToolIdx` (Phase 9.8
 * D-9.8-4 single-expansion semantics) + `historyEndRef` + the auto-scroll
 * useEffect that scrolls to the bottom when a new turn lands.
 *
 * Renders:
 *   - per-turn rendering: ToolHistoryChip for tool turns, USER/agent
 *     formatted text + timestamp + "→ Pi" forward button for non-tool
 *     turns (1.162.3 manual delegation pattern)
 *
 * The compact + clear actions live on the standby bar (VoiceControlsBar)
 * since 1.429.0 — persistent there rather than inside this (mount-gated) log.
 *
 * Extracted from VoicePanel.tsx as part of DESHITTIFICATION Phase 3
 * (2026-05-26). Smart-subcomponent pattern: reads its own slice of
 * `useVoiceStore`, exposes no props. Parent gates the mount via
 * `{historyOpen && <VoiceHistoryLog />}`.
 */
import { useEffect, useRef, useState } from 'react';
import { useVoiceStore } from '@/stores/voiceStore';
import { ToolHistoryChip } from './ToolHistoryChip';
import { formatVoiceTimestamp } from '../../shared/format-voice-timestamp';

export function VoiceHistoryLog() {
  const history = useVoiceStore((s) => s.history);
  const settings = useVoiceStore((s) => s.settings);

  // Phase 9.8 (2026-05-24) — D-9.8-4 LOCKED. Single-expansion at a time;
  // clicking another tool chip closes the first.
  const [expandedToolIdx, setExpandedToolIdx] = useState<number | null>(null);
  const historyEndRef = useRef<HTMLDivElement | null>(null);

  // 1.429.0 — compact + clear moved to VoiceControlsBar (the standby bar).
  // The clear button used to reset expandedToolIdx alongside clearHistory
  // (Phase 9.8 R2 C-2: a stale index could auto-expand the first tool chip
  // of a NEW post-clear history). With clear no longer local to this log,
  // clamp the index here whenever history shrinks past it — covers clear
  // (length → 0), compaction (length → 1), and the 50-turn cap.
  useEffect(() => {
    setExpandedToolIdx((i) => (i !== null && i >= history.length ? null : i));
  }, [history.length]);

  // Auto-scroll the transcript log to the bottom when a new turn lands.
  // jsdom (test env) doesn't implement scrollIntoView — guard before call.
  useEffect(() => {
    if (typeof historyEndRef.current?.scrollIntoView === 'function') {
      historyEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [history]);

  return (
    <div
      className="border-b border-vibe-border bg-vibe-bg flex-1 min-h-0 overflow-y-auto px-3 py-2 text-xs"
      data-testid="voice-history-log"
    >
      {history.length === 0 ? (
        <p className="text-vibe-text-secondary italic">No turns yet — click the mic and speak.</p>
      ) : (
        <>
          {history.map((turn, idx) => (
            <div key={`${turn.timestamp}-${idx}`} className="mb-1">
              {turn.role === 'tool' ? (
                <ToolHistoryChip
                  turn={turn}
                  idx={idx}
                  expanded={expandedToolIdx === idx}
                  onToggle={() =>
                    setExpandedToolIdx(expandedToolIdx === idx ? null : idx)
                  }
                />
              ) : (
                <>
                  <span
                    className={
                      turn.role === 'user'
                        ? 'text-vibe-accent font-semibold'
                        : 'text-green-400 font-semibold'
                    }
                  >
                    {turn.role === 'user' ? 'USER' : settings.voiceAgentName.toUpperCase()}
                  </span>
                  <span
                    className="text-vibe-text-secondary text-[10px] ml-1.5 tabular-nums"
                    data-testid={`voice-history-turn-time-${idx}`}
                  >
                    {formatVoiceTimestamp(turn.timestamp)}
                  </span>
                  <span className="text-vibe-text-secondary">:</span>{' '}
                  <span className="text-vibe-text">{turn.text}</span>
                  {/* 1.162.3 — manual forward-to-Pi button. Deterministic
                      delegation: bypasses LLM + STT-prefix matching. Click
                      forwards this turn's text to the active Pi chat tab
                      via the existing vibe:send-to-pi-chat CustomEvent
                      (consumer wired in ChatPanelPi since 1.162.0).
                      On BOTH user AND bridge turns. */}
                  <button
                    type="button"
                    onClick={() => {
                      window.dispatchEvent(
                        new CustomEvent('vibe:send-to-pi-chat', {
                          detail: { text: turn.text },
                        }),
                      );
                      const h = useVoiceStore.getState().history;
                      const fb = {
                        role: 'bridge' as const,
                        text: '[forwarded to Pi chat]',
                        timestamp: Date.now(),
                      };
                      useVoiceStore.setState({ history: [...h, fb] });
                    }}
                    title="Forward this turn to the active Pi chat tab"
                    aria-label="forward voice turn to Pi chat"
                    data-testid={`voice-history-turn-forward-${idx}`}
                    className="ml-2 px-1.5 py-0 rounded text-[10px] text-vibe-text-secondary hover:text-vibe-text hover:bg-vibe-bg-tertiary"
                  >
                    → Pi
                  </button>
                </>
              )}
            </div>
          ))}
          <div ref={historyEndRef} />
        </>
      )}
    </div>
  );
}
